import { playNotification } from "../socket";
import { desktopNotify, desktopUnread, isDesktopApp } from "./desktop";

/**
 * How the console gets an operator's attention.
 *
 * Three channels, each switchable, remembered per browser: a sound, a count
 * in the tab title (with a dot on the favicon), and a desktop notification
 * while the console is in the background. The desktop permission is only
 * ever asked for from the settings button — a permission prompt on page load
 * is the fastest way to have it refused for good.
 */

export interface NotifyPrefs {
  sound: boolean;
  title: boolean;
  desktop: boolean;
  /** Minutes a customer may wait for an answer before a reminder rings; 0 is off. */
  unansweredAfter: number;
}

const KEY = "covertchat.console.notify";
const BASE_DEFAULTS: NotifyPrefs = { sound: true, title: true, desktop: false, unansweredAfter: 2 };

export function readPrefs(storage: Pick<Storage, "getItem"> | null = safeStorage()): NotifyPrefs {
  // In the Windows app notifications need no browser permission, so they start on.
  const DEFAULTS: NotifyPrefs = { ...BASE_DEFAULTS, desktop: isDesktopApp() };
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<NotifyPrefs>;
    return {
      sound: typeof parsed.sound === "boolean" ? parsed.sound : DEFAULTS.sound,
      title: typeof parsed.title === "boolean" ? parsed.title : DEFAULTS.title,
      desktop: typeof parsed.desktop === "boolean" ? parsed.desktop : DEFAULTS.desktop,
      unansweredAfter:
        typeof parsed.unansweredAfter === "number" && parsed.unansweredAfter >= 0 && parsed.unansweredAfter <= 60
          ? parsed.unansweredAfter
          : DEFAULTS.unansweredAfter,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function savePrefs(prefs: NotifyPrefs, storage: Pick<Storage, "setItem"> | null = safeStorage()): void {
  try {
    storage?.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* storage blocked: the choice lasts for this page only */
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export type DesktopState = "unsupported" | "default" | "granted" | "denied";

export function desktopState(): DesktopState {
  if (isDesktopApp()) return "granted";
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission as DesktopState;
}

export async function requestDesktop(): Promise<DesktopState> {
  if (isDesktopApp()) return "granted";
  if (typeof Notification === "undefined") return "unsupported";
  return (await Notification.requestPermission()) as DesktopState;
}

/** A desktop notification only helps when the operator is not already looking. */
export function shouldShowDesktop(prefs: NotifyPrefs, pageHidden: boolean, permission: DesktopState): boolean {
  return prefs.desktop && pageHidden && permission === "granted";
}

/**
 * Whether the operator is out of sight of the console. In the Windows app a
 * window closed to the tray still reports itself visible (WebView2 is never
 * told), so there focus decides; in a browser a visible tab next to another
 * window is still being watched, so only a hidden tab counts.
 */
export function operatorAway(page: { hidden: boolean; focused: boolean; desktopApp: boolean }): boolean {
  return page.hidden || (page.desktopApp && !page.focused);
}

export function isOperatorAway(): boolean {
  return operatorAway({ hidden: document.hidden, focused: document.hasFocus(), desktopApp: isDesktopApp() });
}

export function notify(input: { title: string; body: string; tag: string; hash?: string; onClick?: () => void }): void {
  const prefs = readPrefs();
  if (prefs.sound) playNotification();
  if (isDesktopApp()) {
    if (shouldShowDesktop(prefs, isOperatorAway(), "granted")) desktopNotify(input.title, input.body.slice(0, 120));
    return;
  }
  if (shouldShowDesktop(prefs, isOperatorAway(), desktopState())) void showDesktop(input);
}

/**
 * Through the service worker when there is one: on a phone the page itself is
 * not allowed to show a notification at all, and the worker is also what
 * brings the console forward when the notification is tapped.
 */
async function showDesktop(input: { title: string; body: string; tag: string; hash?: string; onClick?: () => void }): Promise<void> {
  const body = input.body.slice(0, 120);
  try {
    const registration =
      "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration("/app/") : undefined;
    if (registration) {
      await registration.showNotification(input.title, {
        body,
        tag: input.tag,
        icon: "/app/icon-192.png",
        data: { hash: input.hash ?? "#/inbox" },
      });
      return;
    }
    const n = new Notification(input.title, { body, tag: input.tag });
    n.onclick = () => {
      window.focus();
      input.onClick?.();
      n.close();
    };
  } catch {
    /* no notification is better than an error; the sound still played */
  }
}

/**
 * Makes the console installable and lets notifications open it. Registration
 * failing (an old browser, an insecure origin) leaves the console exactly as
 * it was.
 */
export function registerServiceWorker(onOpen: (hash: string) => void): void {
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const data = event.data as { type?: string; hash?: string } | null;
    if (data?.type === "open" && typeof data.hash === "string" && data.hash.startsWith("#/")) onOpen(data.hash);
  });
  void navigator.serviceWorker.register("/app/sw.js", { scope: "/app/" }).catch(() => undefined);
}

const PLAIN_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%230e7c7b'/%3E%3Cpath d='M9 11h14v9h-8l-4 3v-3H9z' fill='white'/%3E%3C/svg%3E";
const DOT_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%230e7c7b'/%3E%3Cpath d='M9 11h14v9h-8l-4 3v-3H9z' fill='white'/%3E%3Ccircle cx='25' cy='7' r='6' fill='%23c2410c' stroke='white' stroke-width='2'/%3E%3C/svg%3E";

/** The tab's title and icon, reflecting how much is unread. */
export function showUnread(count: number, plainTitle: string, unreadTitle: (n: number) => string): void {
  desktopUnread(count);
  const prefs = readPrefs();
  const on = prefs.title && count > 0;
  document.title = on ? unreadTitle(count) : plainTitle;
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = on ? DOT_ICON : PLAIN_ICON;
}
